import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import PassDesigns from "@/pages/PassDesigns";
import Members from "@/pages/Members";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OnboardingPage from "@/pages/OnboardingPage";
import BillingPage from "@/pages/BillingPage";
import AccountPage from "@/pages/AccountPage";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import axios from "axios";
import "@/index.css";

axios.defaults.withCredentials = true;

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-zinc-950">
    <div className="text-zinc-500 text-sm">Loading...</div>
  </div>
);

// Requires login AND an org (i.e. onboarding already completed). Special
// case: at the exact root path, a logged-out visitor sees the public
// marketing LandingPage instead of being bounced to /login — this is the
// route people actually land on from a Google search or a shared link.
// Every other path under this route (/designs, /members, /settings/billing)
// still redirects logged-out visitors to /login as before.
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) {
    if (location.pathname === "/") return <LandingPage />;
    return <Navigate to="/login" replace />;
  }
  if (!user.org_id) return <Navigate to="/welcome" replace />;

  return children;
};

// Requires login but NOT an org — this is the one route an onboarding-
// pending user is allowed to be on.
const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.org_id) return <Navigate to="/" replace />;

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={user.org_id ? "/" : "/welcome"} replace />;

  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
            <Route path="/welcome" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="designs" element={<PassDesigns />} />
              <Route path="members" element={<Members />} />
              <Route path="settings" element={<Navigate to="/settings/account" replace />} />
              <Route path="settings/account" element={<AccountPage />} />
              <Route path="settings/billing" element={<BillingPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
